import {useEffect, useRef} from 'react';
import {useAtomValue} from 'jotai';
import {userAtom} from '@/store/user';
import {setBrandingCssVariables} from '@/actions/user';

export function AuthBrandingOverride() {
    const user = useAtomValue(userAtom);
    const hasAppliedBrandingRef = useRef(false);

    useEffect(() => {
        const hostname = window.location.hostname;
        const isBrandedSubdomain =
            (hostname.includes('.modelbank.floorplanner.com') &&
                hostname !== 'modelbank.floorplanner.com') ||
            (hostname.includes('.localhost') && hostname !== 'localhost');

        if (isBrandedSubdomain) return;

        if (user?.branding && !hasAppliedBrandingRef.current) {
            hasAppliedBrandingRef.current = true;
            document.body.classList.add('branding-transition');
            setBrandingCssVariables(user.branding);

            let cancelled = false;

            const transitionTimer = setTimeout(() => {
                if (cancelled) return;

                const transitionDuration = '1.2s';
                const transitionEasing = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

                const authSidebar = document.querySelector(
                    '[data-auth-sidebar]'
                ) as HTMLElement;
                const imageSection = document.querySelector(
                    '[class*="image"]'
                ) as HTMLElement;
                const logoImg = document.querySelector(
                    'header img'
                ) as HTMLImageElement;
                const userSection = document.querySelector(
                    '[class*="userSection"]'
                ) as HTMLElement;

                const promises: Promise<void>[] = [];

                if (authSidebar && user.branding.background_color) {
                    authSidebar.style.backgroundColor =
                        user.branding.background_color;
                }

                const loginImageUrl =
                    user.branding.login_image || user.branding.background_image;
                if (loginImageUrl && imageSection) {
                    const originalBackground =
                        imageSection.style.backgroundImage ||
                        getComputedStyle(imageSection).backgroundImage;

                    const bottomLayer = document.createElement('div');
                    const topLayer = document.createElement('div');

                    const layerStyles = {
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                    };

                    Object.assign(bottomLayer.style, layerStyles);
                    bottomLayer.style.backgroundImage = originalBackground;
                    bottomLayer.style.zIndex = '1';

                    Object.assign(topLayer.style, layerStyles);
                    topLayer.style.opacity = '0';
                    topLayer.style.setProperty(
                        'transition',
                        `opacity ${transitionDuration} ${transitionEasing}`,
                        'important'
                    );
                    topLayer.style.zIndex = '2';

                    imageSection.style.position = 'relative';
                    imageSection.style.backgroundImage = 'none';
                    imageSection.appendChild(bottomLayer);
                    imageSection.appendChild(topLayer);

                    promises.push(
                        new Promise((resolve) => {
                            const img = new Image();
                            img.onload = () => {
                                topLayer.style.backgroundImage = `url(${loginImageUrl})`;
                                resolve();
                            };
                            img.src = loginImageUrl;
                        })
                    );
                }

                const logoUrl =
                    user.branding.logo_image ||
                    user.branding.settings?.light_logo_image?.url;
                if (logoUrl && logoImg) {
                    logoImg.style.setProperty(
                        'transition',
                        `opacity ${transitionDuration} ${transitionEasing}`,
                        'important'
                    );

                    promises.push(
                        new Promise((resolve) => {
                            const newLogo = new Image();
                            newLogo.onload = () => resolve();
                            newLogo.src = logoUrl;
                        })
                    );
                }

                if (userSection && !userSection.style.opacity) {
                    userSection.style.opacity = '0';
                    userSection.style.setProperty(
                        'transition',
                        `opacity ${transitionDuration} ${transitionEasing}`,
                        'important'
                    );
                }

                Promise.all(promises).then(() => {
                    if (cancelled) return;

                    requestAnimationFrame(() => {
                        if (cancelled) return;

                        if (user.branding.dark) {
                            document.body.style.setProperty(
                                '--color-primary',
                                user.branding.dark
                            );
                        }

                        if (loginImageUrl && imageSection) {
                            const topLayer = imageSection
                                .children[1] as HTMLElement;
                            if (topLayer) topLayer.style.opacity = '1';
                        }

                        if (logoUrl && logoImg) {
                            logoImg.style.opacity = '0';

                            setTimeout(() => {
                                if (cancelled) return;
                                logoImg.src = logoUrl;
                                logoImg.alt = `${user.branding.name || 'Brand'} Logo`;
                                logoImg.style.opacity = '1';
                            }, 600);
                        }

                        if (userSection) {
                            userSection.style.opacity = '1';
                        }
                    });

                    setTimeout(() => {
                        if (cancelled) return;
                        if (loginImageUrl && imageSection) {
                            imageSection.style.backgroundImage = `url(${loginImageUrl})`;
                            while (imageSection.children.length > 0) {
                                imageSection.removeChild(
                                    imageSection.children[0]
                                );
                            }
                        }
                    }, 1300);
                });
            }, 400);

            const cleanupTimer = setTimeout(() => {
                if (cancelled) return;
                document.body.classList.remove('branding-transition');
            }, 2000);

            return () => {
                cancelled = true;
                clearTimeout(transitionTimer);
                clearTimeout(cleanupTimer);

                // Remove all CSS overrides so they don't leak into the
                // next layout (e.g. dashboard) after client-side navigation.
                document.documentElement.style.removeProperty(
                    '--color-primary'
                );
                document.documentElement.style.removeProperty(
                    '--color-secondary'
                );
                document.body.style.removeProperty('--color-primary');
                document.body.classList.remove('branding-transition');
            };
        }
    }, [user]);

    return null;
}
